import { Controller } from "@hotwired/stimulus"
import { queryStringAssign } from "../shared/formHelper"

/**
 * Layout 2Depth Menu Controller
 * 
 * 2단계 메뉴 레이아웃의 인터랙션을 관리하는 Stimulus 컨트롤러
 * - GNB Rail (1depth): 데스크탑에서 항상 표시, 아이템 0~1개면 숨김
 * - LNB Sidebar (2depth): 데스크탑에서 토글 가능, 모바일에서 드로어
 * - Mobile Tab Bar: 모바일에서 하단 탭 네비게이션 (grid 5컬럼)
 * 
 * Targets:
 * - lnb: LNB 사이드바 요소
 * - mainContent: 메인 컨텐츠 영역
 * - backdrop: 오버레이 배경
 * - sectionItems: GNB Rail 메뉴 리스트 (ul 요소) - 아이템 개수에 따라 숨김 처리
 * - lnbItems: LNB 메뉴 리스트 (ul 요소) - 추후 badge 등 동적 처리용
 * - tabItems: 모바일 탭 리스트 (ul 요소) - 5개 이상 시 확장 버튼 자동 추가
 * 
 * Values:
 * - lnbOpen: LNB 열림 상태 (Boolean)
 * - tabExpanded: 모바일 탭 확장 상태 (Boolean)
 * - breakpoint: 모바일/데스크탑 분기점 (Number, default: 640)
 * - tabVisibleCount: 모바일 탭에서 기본 표시 아이템 수 (Number, default: 4)
 * 
 * CSS Classes (on root element):
 * - l2s-section-hidden: GNB가 숨겨진 상태 (아이템 0~1개)
 * - l2s-tab-expanded: 모바일 탭 확장 상태 (스크롤 활성화)
 */
export default class extends Controller {
  static targets = [
    "gnb",
    "lnb",
    "mainContent",
    "backdrop",
    "sectionItems",
    "lnbItems",
    "tabItems",
    "lnbFrame"
  ]

  static values = {
    lnbOpen: { type: Boolean, default: true },
    tabExpanded: { type: Boolean, default: false },
    breakpoint: { type: Number, default: 640 },
    tabVisibleCount: { type: Number, default: 4 }
  }

  connect() {
    this._initialized = true

    // 모바일에서 초기 상태: 닫힘
    if (this.isMobile()) {
      this.lnbOpenValue = false
    }

    this.updateUI()

    // 화면 크기 변경 감지
    this.resizeHandler = this.handleResize.bind(this)
    window.addEventListener("resize", this.resizeHandler)

    // ESC 키로 닫기
    this.escapeHandler = this.handleEscape.bind(this)
    document.addEventListener("keydown", this.escapeHandler)
  }

  disconnect() {
    window.removeEventListener("resize", this.resizeHandler)
    document.removeEventListener("keydown", this.escapeHandler)
  }

  // ========================================
  // Target Connected Callbacks
  // ========================================

  /**
   * sectionItems(GNB 메뉴 리스트) 연결 시 아이템 개수 체크
   * 0개 또는 1개면 l2s-section-hidden 클래스 추가
   */
  sectionItemsTargetConnected(element) {
    const itemCount = element.querySelectorAll("li").length
    if (itemCount <= 1) {
      this.element.classList.add("l2s-section-hidden")
    } else {
      this.element.classList.remove("l2s-section-hidden")
    }
  }

  /**
   * tabItems(모바일 탭 리스트) 연결 시
   * 서버에서 렌더링된 더보기 버튼(.--more)의 활성 상태를 확인하여
   * localStorage에서 확장 상태를 복원
   */
  tabItemsTargetConnected(element) {
    // 서버에서 렌더링된 더보기 버튼(.--more)이 있고,
    // 숨겨진 영역에 활성 아이템(.--has-active)이 있으면 localStorage 상태 복원
    const moreButton = element.querySelector(":scope > li.--more")
    if (moreButton && moreButton.classList.contains("--has-active")) {
      this.tabExpandedValue = this.loadTabMoreExpanded()
    }
  }

  // ========================================
  // Value Changed Callbacks
  // ========================================

  lnbOpenValueChanged() {
    if (this._initialized) this.updateUI()
  }

  tabExpandedValueChanged() {
    // 루트 요소에 확장 상태 클래스 토글
    if (this.tabExpandedValue) {
      this.element.classList.add("l2s-tab-expanded")
    } else {
      this.element.classList.remove("l2s-tab-expanded")
    }
  }

  // ========================================
  // Actions
  // ========================================

  /**
   * LNB 토글 (데스크탑: 접기/펴기, 모바일: 열기/닫기)
   */
  toggleLnb() {
    this.lnbOpenValue = !this.lnbOpenValue
  }

  /**
   * LNB 열기
   */
  openLnb() {
    this.lnbOpenValue = true
  }

  /**
   * LNB 닫기
   */
  closeLnb() {
    this.lnbOpenValue = false
  }

  /**
   * 모바일 탭 확장 토글
   * 토글 시 현재 활성 탭 경로와 확장 상태를 localStorage에 저장
   */
  toggleExpand() {
    this.tabExpandedValue = !this.tabExpandedValue
    this.saveTabMoreExpanded()
  }

  /**
   * 모바일 탭 확장 열기
   */
  openExpand() {
    this.tabExpandedValue = true
  }

  /**
   * 모바일 탭 확장 닫기
   */
  closeExpand() {
    this.tabExpandedValue = false
  }

  /**
   * GNB 섹션 선택 시 페이지 이동 없이 LNB 교체
   * - turbo-frame src를 변경하여 서버에서 새 LNB 내용 로드
   * - GNB 활성 상태 즉시 갱신
   * - 모바일에서 LNB 드로어 열기
   */
  selectSection(event) {
    event.preventDefault()
    const anchor = event.currentTarget
    const sectionId = anchor.dataset.sectionId

    // 이미 선택된 섹션이면 무시
    if (anchor.classList.contains("--selected")) return

    // GNB 선택 상태 갱신
    this._updateGnbActiveState(anchor)

    // LNB turbo-frame src 변경 → 서버에서 새 LNB 콘텐츠 로드
    // data-src는 Turbine이 항상 정의하는 초기 src URL
    if (this.hasLnbFrameTarget) {
      const frame = this.lnbFrameTarget
      frame.removeAttribute("disabled")
      frame.src = queryStringAssign(frame.dataset.src, { section_id: sectionId })
    }

    // LNB가 닫혀있으면 열기
    if (!this.lnbOpenValue) {
      this.lnbOpenValue = true
    }
  }

  /**
   * 배경 클릭 시 모든 메뉴 닫기
   */
  closeAll() {
    if (this.isMobile()) {
      this.lnbOpenValue = false
    }
    this.tabExpandedValue = false
  }

  // ========================================
  // Private Methods
  // ========================================

  /**
   * GNB 선택 상태를 업데이트 (--selected 테두리)
   * --active(현재 페이지 bg)는 유지하고, --selected만 토글
   */
  _updateGnbActiveState(selectedAnchor) {
    if (!this.hasSectionItemsTarget) return

    // 기존 선택 상태 제거
    const allItems = this.sectionItemsTarget.querySelectorAll(".l2s-gnb-item a")
    allItems.forEach(item => {
      item.classList.remove("--selected")
    })

    // 선택된 항목에 테두리 표시
    selectedAnchor.classList.add("--selected")
  }

  isMobile() {
    return window.innerWidth < this.breakpointValue
  }

  handleResize() {
    // 데스크탑으로 전환 시 backdrop 닫기 및 탭 확장 닫기
    if (!this.isMobile()) {
      if (this.hasBackdropTarget) {
        this.backdropTarget.dataset.state = ""
      }
      this.tabExpandedValue = false
    }
    this.updateUI()
  }

  handleEscape(event) {
    if (event.key === "Escape") {
      if (this.tabExpandedValue) {
        this.closeExpand()
      } else if (this.isMobile() && this.lnbOpenValue) {
        this.closeLnb()
      }
    }
  }

  updateUI() {
    if (!this.hasLnbTarget) return

    if (this.isMobile()) {
      // 모바일 UI - GNB와 LNB가 함께 드로어로 열림
      this.lnbTarget.dataset.state = this.lnbOpenValue ? "open" : ""

      // GNB도 모바일에서 함께 슬라이드인
      if (this.hasGnbTarget) {
        this.gnbTarget.style.transform = this.lnbOpenValue ? "translateX(0)" : ""
      }

      if (this.hasBackdropTarget) {
        this.backdropTarget.dataset.state = this.lnbOpenValue ? "open" : ""
      }

      if (this.hasMainContentTarget) {
        this.mainContentTarget.dataset.lnbCollapsed = "false"
      }

      // 모바일에서는 l2s-lnb-collapsed 클래스 제거
      this.element.classList.remove("l2s-lnb-collapsed")
    } else {
      // 데스크탑 UI
      this.lnbTarget.dataset.state = this.lnbOpenValue ? "" : "collapsed"

      if (this.hasBackdropTarget) {
        this.backdropTarget.dataset.state = ""
      }

      if (this.hasMainContentTarget) {
        this.mainContentTarget.dataset.lnbCollapsed = this.lnbOpenValue ? "false" : "true"
      }

      // PC에서 LNB 닫혔을 때 클래스 추가 (GNB에 열기 버튼 표시용)
      if (this.lnbOpenValue) {
        this.element.classList.remove("l2s-lnb-collapsed")
      } else {
        this.element.classList.add("l2s-lnb-collapsed")
      }
    }
  }

  // ========================================
  // Tab More State (localStorage 연동)
  // 더보기 열림/닫힘 상태만 단순 저장
  // ========================================

  static TAB_MORE_STORAGE_KEY = "l2s-tab-more-expanded"

  /**
   * 더보기 확장 상태를 localStorage에 저장 (boolean)
   */
  saveTabMoreExpanded() {
    try {
      localStorage.setItem(
        this.constructor.TAB_MORE_STORAGE_KEY,
        this.tabExpandedValue ? "1" : "0"
      )
    } catch { /* localStorage 사용 불가 시 무시 */ }
  }

  /**
   * localStorage에서 더보기 확장 상태 로드
   * 저장된 값이 없으면 true (기본 확장)
   */
  loadTabMoreExpanded() {
    try {
      const value = localStorage.getItem(this.constructor.TAB_MORE_STORAGE_KEY)
      return value === null ? true : value === "1"
    } catch {
      return true
    }
  }
}
